import React, { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Card, SxProps } from '@mui/material';

import {
  ADMIN_CHAT_VIEW_MODES,
  adminChatGetConversations,
  getAdminChatState,
  resetAdminChatMessages,
  setAdminChatSelectedConversation,
  setAdminChatViewMode,
} from '../../../../store/V2/AdminChatSlice';
import { useAppSelector } from '../../../../hooks';
import { IConversation } from '../../../../store/V2/ChatSlice';
import AdminChatHeader from './components/AdminChatHeader';
import AdminChatConversationsView from './views/AdminChatConversationsView';
import AdminChatSelectedConversationView from './views/AdminChatSelectedConversationView';
import AdminChatNewConversationView from './views/AdminChatNewConversationView';
import {
  getCourseStudentsAsAdmin,
  resetAdminCourseStudentsData,
} from '../../../../store/V2/CourseStudentsSlice';
import {
  getCourseTutorsAsAdmin,
  resetAdminCourseTutorsData,
} from '../../../../store/V2/CourseTutorsSlice';

interface AdminChatBoxProps {
  isChatOpen: boolean;
  toggleChatCollapse: () => void;
}

const containerStyles: SxProps = {
  flexDirection: 'column',
  borderRadius: '20px',
  height: 'calc(100vh - 250px)',
  minHeight: '500px',
  border: '1px solid #E2E8F0',
};

const AdminChatBox: React.FunctionComponent<AdminChatBoxProps> = ({
  isChatOpen,
  toggleChatCollapse,
}) => {
  const dispatch = useDispatch();
  const urlParams = useParams();
  const location = useLocation();

  // Store
  const adminChatState = useAppSelector(getAdminChatState);

  const {
    viewMode,
    selectedConversation,
    adminChatConversationsData,
  } = adminChatState;

  const questyStringParams = new URLSearchParams(location.search);
  const urlCourseId = urlParams.id;
  const urlConversationId = questyStringParams.get('conversation') || '';

  useEffect(() => {
    if (urlCourseId) {
      dispatch(
        adminChatGetConversations({
          courseId: urlCourseId,
        })
      );

      dispatch(
        getCourseStudentsAsAdmin({
          limit: 200,
          offset: 0,
          course: urlCourseId,
          status: 'ENABLE',
        })
      );

      dispatch(
        getCourseTutorsAsAdmin({
          limit: 200,
          offset: 0,
          course: urlCourseId,
          status: 'ENABLE',
        })
      );
    }

    return () => {
      dispatch(resetAdminCourseTutorsData());
      dispatch(resetAdminCourseStudentsData());
    };
  }, [urlCourseId]);

  useEffect(() => {
    if (
      adminChatConversationsData.list &&
      adminChatConversationsData.list.length &&
      urlConversationId
    ) {
      const matchingConversation = adminChatConversationsData.list.find(
        (item) => item._id === urlConversationId
      );

      if (matchingConversation) {
        dispatch(resetAdminChatMessages());
        dispatch(setAdminChatViewMode(ADMIN_CHAT_VIEW_MODES.CONVERSATION));
        dispatch(setAdminChatSelectedConversation(matchingConversation));
      }
    }
  }, [urlConversationId]);

  const onConversationSelect = (conversation: IConversation) => {
    dispatch(resetAdminChatMessages());

    dispatch(setAdminChatSelectedConversation(conversation));
    dispatch(setAdminChatViewMode(ADMIN_CHAT_VIEW_MODES.CONVERSATION));
  };

  const toggleToConversationListView = () => {
    dispatch(resetAdminChatMessages());
    dispatch(setAdminChatSelectedConversation(undefined));
    dispatch(setAdminChatViewMode(ADMIN_CHAT_VIEW_MODES.CONVERSATION_LIST));
  };

  const toggleToNewConversationView = () => {
    dispatch(setAdminChatViewMode(ADMIN_CHAT_VIEW_MODES.NEW_CONVERSATION));
  };

  const renderBody = () => {
    let content: JSX.Element | null = null;

    switch (viewMode) {
      case ADMIN_CHAT_VIEW_MODES.CONVERSATION_LIST:
        content = (
          <AdminChatConversationsView
            onConversationSelect={onConversationSelect}
          />
        );
        break;

      case ADMIN_CHAT_VIEW_MODES.CONVERSATION:
        content = selectedConversation ? (
          <AdminChatSelectedConversationView
            conversation={selectedConversation}
          />
        ) : null;
        break;

      case ADMIN_CHAT_VIEW_MODES.NEW_CONVERSATION:
        content = <AdminChatNewConversationView />;
        break;

      default:
        content = null;
        break;
    }

    return content;
  };

  return (
    <Card
      variant="outlined"
      sx={{
        ...containerStyles,
        width: isChatOpen ? '350px' : '40px',
        display: isChatOpen ? 'flex' : 'none',
      }}
    >
      {viewMode ? (
        <AdminChatHeader
          viewMode={viewMode}
          toggleChatCollapse={toggleChatCollapse}
          handleBack={toggleToConversationListView}
          selectedConversation={selectedConversation}
          onAddNewConversation={toggleToNewConversationView}
        />
      ) : null}

      {renderBody()}
    </Card>
  );
};

export default AdminChatBox;
